/**
 * Importing and managing somebody else's overlays.
 *
 * The panel is shaped around two things a layer is not.
 *
 * **It is not a track, so nothing removes it but a person.** A layer does not
 * decay; the list therefore shows every one that was ever imported, including
 * lapsed ones, and every one has a delete. An overlay that quietly aged off
 * would be a boundary that stopped being drawn without anybody deciding.
 *
 * **An expiry is not implied.** The field is optional and empty means the
 * author gave none, which the list renders as "no expiry given" rather than as
 * a date or a dash that could be read as one.
 *
 * Import is check-then-commit. The same file is offered to the server twice —
 * once as a dry run that stores nothing, once for real — because an overlay is
 * far easier to argue with before it is on everybody's map than after.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { deleteLayer, getLayers, importLayer, type Layer } from '@/api/layers';
import { useHasRole } from '@/stores/authStore';

const WARN = 'var(--caution)';
const MUTED = 'var(--muted-foreground-3)';

/** Slow: a layer changes when a person imports one, not on a telemetry beat. */
const POLL_MS = 15_000;

function whenText(layer: Layer): string {
  if (layer.validUntil === null) return 'no expiry given';
  const when = new Date(layer.validUntil * 1000);
  return `${layer.expired ? 'lapsed' : 'until'} ${when.toISOString().slice(0, 16).replace('T', ' ')}Z`;
}

export function OverlaysPanel(): ReactNode {
  const canImport = useHasRole('operator');
  const [layers, setLayers] = useState<Layer[]>([]);
  const [content, setContent] = useState('');
  const [name, setName] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const result = await getLayers();
    if (result.ok) setLayers(result.data.layers);
  }, []);

  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => void reload(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [reload]);

  async function send(dryRun: boolean) {
    setBusy(true);
    setProblems([]);
    setNotice(null);
    const result = await importLayer({ content, name: name.trim(), dryRun });
    setBusy(false);
    if (!result.ok) {
      setProblems([result.error.message]);
      return;
    }
    if (!result.data.accepted) {
      // Every problem at once. An operator fixing one file should not discover
      // the next error on the next attempt.
      setProblems(result.data.problems);
      return;
    }
    if (dryRun) {
      setNotice(`Reads cleanly: ${result.data.featureCount} feature(s). Nothing stored yet.`);
      return;
    }
    setNotice(`Imported ${result.data.featureCount} feature(s) as ${result.data.name}.`);
    setContent('');
    setName('');
    await reload();
  }

  async function remove(layer: Layer) {
    setBusy(true);
    await deleteLayer(layer.layerId);
    setBusy(false);
    await reload();
  }

  return (
    <div className="flex flex-col gap-2.5 py-1">
      <div className="flex items-center gap-2">
        <span className="label-mono text-[9px]">IMPORTED OVERLAYS</span>
        <span className="ml-auto label-mono text-[8px]" style={{ color: MUTED }}>
          {layers.length} ON THE MAP
        </span>
      </div>

      {layers.length === 0 ? (
        <p className="text-[10.5px] leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          No overlays imported. Paste a GeoJSON or KML boundary below and it appears on the map in
          amber — imported geometry, never a zone this system enforces.
        </p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {layers.map((layer) => (
            <div key={layer.layerId} className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[10.5px]" style={{ color: 'var(--muted-foreground)' }}>
                  {layer.name}
                </p>
                <p className="label-mono text-[8.5px]" style={{ color: layer.expired ? WARN : MUTED }}>
                  {layer.featureCount} feature{layer.featureCount === 1 ? '' : 's'} ·{' '}
                  {whenText(layer)}
                </p>
              </div>
              {canImport ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void remove(layer)}
                  className="shrink-0 rounded-[8px] border px-2 py-1 label-mono text-[9px] disabled:opacity-40"
                  style={{ borderColor: 'var(--border-nested)' }}
                  aria-label={`Remove overlay ${layer.name}`}
                >
                  REMOVE
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {canImport ? (
        <>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Name (optional — the file's own name is used)"
            aria-label="Overlay name"
            className="rounded-[8px] border bg-transparent px-2.5 py-1.5 text-[10.5px]"
            style={{ borderColor: 'var(--border-nested)', color: '#f4f4f5' }}
          />
          <textarea
            value={content}
            onChange={(event) => setContent(event.target.value)}
            placeholder="Paste GeoJSON or KML"
            aria-label="Overlay file contents"
            rows={4}
            className="scroll-thin rounded-[8px] border bg-transparent px-2.5 py-1.5 font-mono text-[10px]"
            style={{ borderColor: 'var(--border-nested)', color: '#f4f4f5' }}
          />
          <div className="flex gap-1.5">
            <button
              type="button"
              disabled={!content.trim() || busy}
              onClick={() => void send(true)}
              className="rounded-[8px] border px-2.5 py-1 label-mono text-[9px] disabled:opacity-40"
              style={{ borderColor: 'var(--border-nested)' }}
              title="Reports what the file would do and stores nothing."
            >
              CHECK
            </button>
            <button
              type="button"
              disabled={!content.trim() || busy}
              onClick={() => void send(false)}
              className="rounded-[8px] border px-2.5 py-1 label-mono text-[9px] disabled:opacity-40"
              style={{ borderColor: 'var(--border-nested)' }}
            >
              {busy ? 'WORKING…' : 'IMPORT'}
            </button>
          </div>
        </>
      ) : null}

      {notice ? (
        <p className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
          {notice}
        </p>
      ) : null}

      {problems.length > 0 ? (
        <div className="flex flex-col gap-1">
          <span className="label-mono text-[9px]" style={{ color: WARN }}>
            NOTHING WAS IMPORTED
          </span>
          {problems.map((problem) => (
            <p key={problem} className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
              {problem}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default OverlaysPanel;
